import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import { FiUser, FiMail, FiLock, FiSave, FiX, FiAlertCircle, FiCheckCircle, FiUsers } from 'react-icons/fi';
import { AdminUserService } from '../../services/api';
import { USER_CATEGORIES } from '../../constants/userCategories';

interface UserFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  role: 'ADMIN' | 'MEMBER';
  phoneNumber: string;
  lineId: string;
  userCategory: string;  // Add user category
}

interface FormErrors {
  email?: string;
  password?: string;
  confirmPassword?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

const UserAddPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: 'MEMBER',
    phoneNumber: '',
    lineId: '',
    userCategory: 'individual',  // Default to individual
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'กรุณาระบุอีเมล';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'รูปแบบอีเมลไม่ถูกต้อง';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'กรุณาระบุรหัสผ่าน';
    } else if (formData.password.length < 8) {
      newErrors.password = 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'รหัสผ่านไม่ตรงกัน';
    }

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'กรุณาระบุชื่อ';
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'กรุณาระบุนามสกุล';
    }

    // Phone number validation (optional but if provided, must be valid)
    if (formData.phoneNumber && !/^[0-9]{10}$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'เบอร์โทรต้องเป็นตัวเลข 10 หลัก';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous messages
    setApiError(null);
    setSuccessMessage(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Call API to create user
      const response = await AdminUserService.createUser({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: formData.role,
        phone: formData.phoneNumber || undefined,
        lineId: formData.lineId || undefined,
        user_category: formData.userCategory.toUpperCase(),  // Transform to uppercase for backend
      });

      // Show success message
      setSuccessMessage(`สร้างผู้ใช้ ${response.email} สำเร็จ!`);

      // Redirect to users list after 1.5 seconds
      setTimeout(() => {
        navigate('/admin/users');
      }, 1500);

    } catch (error: any) {
      console.error('Error creating user:', error);

      // Extract error message from API response
      let errorMessage = 'เกิดข้อผิดพลาดในการสร้างผู้ใช้';

      // Priority 1: Check structured error response from backend
      if (error.response?.data?.error?.details) {
        // Backend returns structured error with details
        errorMessage = error.response.data.error.details;
      } else if (error.response?.data?.detail) {
        // FastAPI default error format
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        // Custom message field
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // JavaScript error message from interceptor
        errorMessage = error.message;
      }

      // Handle specific error types
      if (error.code === 'NETWORK_ERROR') {
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
      } else if (error.code === 'FORBIDDEN') {
        errorMessage = 'คุณไม่มีสิทธิ์ในการสร้างผู้ใช้ กรุณาติดต่อผู้ดูแลระบบ';
      } else if (error.type === 'server') {
        errorMessage = 'เซิร์ฟเวอร์ขัดข้อง กรุณาลองใหม่อีกครั้งในภายหลัง';
      }

      setApiError(errorMessage);

      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/users');
  };

  return (
    <AdminLayout title="เพิ่มผู้ใช้ใหม่">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            เพิ่มผู้ใช้ใหม่
          </h1>
          <p className="text-gray-600">
            สร้างบัญชีผู้ใช้ใหม่สำหรับเข้าถึงระบบ
          </p>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg p-4 flex items-center space-x-2">
            <FiCheckCircle className="w-5 h-5 text-green-600" />
            <span className="font-medium">{successMessage}</span>
          </div>
        )}

        {apiError && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center space-x-2">
            <FiAlertCircle className="w-5 h-5 text-red-600" />
            <span className="font-medium">{apiError}</span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiMail className="inline w-4 h-4 mr-1" />
              อีเมล <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="user@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                <FiAlertCircle className="w-4 h-4" />
                <span>{errors.email}</span>
              </p>
            )}
          </div>

          {/* Password */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiLock className="inline w-4 h-4 mr-1" />
                รหัสผ่าน <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.password ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="อย่างน้อย 8 ตัวอักษร"
                />
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                  <FiAlertCircle className="w-4 h-4" />
                  <span>{errors.password}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiLock className="inline w-4 h-4 mr-1" />
                ยืนยันรหัสผ่าน <span className="text-red-600">*</span>
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="ยืนยันรหัสผ่าน"
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                  <FiAlertCircle className="w-4 h-4" />
                  <span>{errors.confirmPassword}</span>
                </p>
              )}
            </div>
          </div>

          {/* Show/Hide Password */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="showPassword"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
              className="mr-2"
            />
            <label htmlFor="showPassword" className="text-sm text-gray-600">
              แสดงรหัสผ่าน
            </label>
          </div>

          {/* First Name & Last Name */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiUser className="inline w-4 h-4 mr-1" />
                ชื่อ <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="ชื่อ"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                  <FiAlertCircle className="w-4 h-4" />
                  <span>{errors.firstName}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FiUser className="inline w-4 h-4 mr-1" />
                นามสกุล <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="นามสกุล"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                  <FiAlertCircle className="w-4 h-4" />
                  <span>{errors.lastName}</span>
                </p>
              )}
            </div>
          </div>

          {/* Phone Number & Line ID */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                เบอร์โทรศัพท์
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="0812345678"
                maxLength={10}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600 flex items-center space-x-1">
                  <FiAlertCircle className="w-4 h-4" />
                  <span>{errors.phoneNumber}</span>
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Line ID
              </label>
              <input
                type="text"
                name="lineId"
                value={formData.lineId}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="@username"
              />
              <p className="mt-1 text-sm text-gray-500">
                (ไม่บังคับ) Line ID สำหรับการติดต่อ
              </p>
            </div>
          </div>

          {/* User Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FiUsers className="inline w-4 h-4 mr-1" />
              ประเภทผู้ใช้ <span className="text-red-600">*</span>
            </label>
            <select
              name="userCategory"
              value={formData.userCategory}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {USER_CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.icon} {category.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              เลือกประเภทผู้ใช้เพื่อปรับแต่งประสบการณ์การใช้งาน
            </p>
          </div>

          {/* Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              บทบาท <span className="text-red-600">*</span>
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="MEMBER">สมาชิก (Member)</option>
              <option value="ADMIN">ผู้ดูแลระบบ (Admin)</option>
            </select>
            <p className="mt-1 text-sm text-gray-500">
              {formData.role === 'ADMIN'
                ? 'มีสิทธิ์เข้าถึงและจัดการระบบทั้งหมด'
                : 'สามารถค้นหาและ claim บริษัทได้'}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-4 border-t">
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center space-x-2"
            >
              <FiX className="w-5 h-5" />
              <span>ยกเลิก</span>
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
            >
              <FiSave className="w-5 h-5" />
              <span>{loading ? 'กำลังบันทึก...' : 'สร้างผู้ใช้'}</span>
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default UserAddPage;
